import PropTypes from "prop-types";
import { forwardRef } from "react";

import styled from "styled-components/macro";

import { AutocompleteProvider } from "./AutocompleteContext";

/**
 * Wrapper that gives `useAddressAutocomplete` a container ref to listen on
 * for `focusout`, and the relative-position anchor that the ShadowText
 * overlay and the verified Badge use for absolute positioning.
 *
 * Surfaces wrap their existing address-fields grid with this root, attach
 * `streetRef` to the Street input, and drop the Badge / ShadowText into the
 * resulting layout.
 *
 * Pass `value={autocomplete}` (the full `useAddressAutocomplete` return
 * value) to expose the hook output via context so the
 * `Autocomplete{Street,City,State,Zip}Field` wrappers auto-wire their
 * autocomplete props. Without `value`, children render normally and any
 * `useAutocompleteContext()` call throws — useful for legacy compose-by-
 * hand surfaces.
 */
const AddressAutocompleteRoot = forwardRef(function AddressAutocompleteRoot(
  { children, className, style, value },
  ref
) {
  let body = children;
  if (value) {
    body = <AutocompleteProvider value={value}>{body}</AutocompleteProvider>;
  }
  return (
    <Root ref={ref} className={className} style={style}>
      {body}
    </Root>
  );
});

const Root = styled.div`
  position: relative;
  /* Scopes all z-indexes inside the address card (focus rings, badge,
     shadow-text overlay) to this stacking context so consumers can use
     small intentional values like 1 / 2 without competing with the
     surrounding chrome (e.g. a popover wrapper). */
  isolation: isolate;
  /* Lets descendants switch grid templates via @container queries
     instead of measuring with a ref. Without this, a ref-driven layout
     decision is one paint late, which snaps the address card height at
     the end of any height-animating reveal. */
  container-type: inline-size;
`;

AddressAutocompleteRoot.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  style: PropTypes.object,
  value: PropTypes.object,
};

export default AddressAutocompleteRoot;
